import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  AlertTriangle,
  Droplets,
  Wind,
  CloudLightning,
} from "lucide-react";
import { SEO } from "@/components/SEO";

export default function EstevanRepair() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does roof repair cost in Estevan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Roof repairs in Estevan usually cost between $300 and $1,500 depending on the severity of the damage. We provide free inspections and detailed, fixed-price quotes.",
        },
      },
      {
        "@type": "Question",
        name: "Can you match my existing shingles?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, we work with all major suppliers to find the closest possible match to your existing shingles, ensuring the repair blends in nicely with the rest of your roof.",
        },
      },
      {
        "@type": "Question",
        name: "Do you repair roof leaks around chimneys?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, chimney flashing failure is a common cause of leaks. We can remove the old, rusted flashing and install new, custom-fit metal flashing to ensure a watertight seal.",
        },
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": "https://www.saskroofingcompany.com/#localbusiness",
    name: "Sask Roofing Company",
    url: "https://www.saskroofingcompany.com/",
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: "Estevan",
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roof Repair",
    provider: {
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Estevan",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.saskroofingcompany.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: "https://www.saskroofingcompany.com/areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Estevan",
        item: "https://www.saskroofingcompany.com/areas/estevan",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Repair",
        item: "https://www.saskroofingcompany.com/areas/estevan/repair",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Roof Repair Estevan | Sask Roofing Company"
        description="Expert roof repair in Estevan, SK. We fix wind damage, leaks, and missing shingles quickly and professionally. Get a free, honest repair estimate today."
        schema={[
          faqSchema,
          localBusinessSchema,
          serviceSchema,
          breadcrumbSchema,
        ]}
      />
      <div className="flex flex-col min-h-screen">
        <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/6e2ca68f-b65c-4e53-8f1f-3c188b15b51c.png&output=webp&q=60"
              alt="Estevan Roof Repair"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Roof Repair Estevan
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                High winds and severe weather take a toll on Estevan roofs. If
                you have missing shingles, flashing issues, or an active leak,
                our local team provides fast, reliable, and permanent repairs.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">Get a Free Repair Estimate</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  Fixing Wind & Storm Damage
                </h2>
                <p className="text-lg text-muted-foreground">
                  Estevan is known for its intense weather. When a storm rolls
                  through, it can easily lift or tear off shingles, leaving your
                  roof deck exposed. Prompt repairs are essential to prevent
                  costly interior damage.
                </p>
                <div className="space-y-4 pt-4">
                  <h3 className="text-xl font-semibold">
                    We specialize in repairing:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Wind className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Wind Damage:</strong> Replacing blown-off,
                        creased, or unsealed shingles.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Droplets className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Active Leaks:</strong> Finding and fixing the
                        source of water entering your home.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Flashing Issues:</strong> Repairing leaks around
                        chimneys, vents, and skylights.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CloudLightning className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Hail Damage:</strong> Assessing and patching
                        localized impact damage.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/40828d16-935f-4087-8b3c-2dd81ee3ce2a.png&output=webp&q=60"
                  alt="Inspecting a roof for wind damage in Estevan"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
              <h2 className="text-3xl font-bold">
                Why Choose Us for Estevan Roof Repair?
              </h2>
              <p className="text-lg text-muted-foreground">
                We provide honest advice. If a simple repair will solve the
                problem, we won't try to sell you a completely new roof.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Expert Diagnostics</h3>
                <p className="text-muted-foreground">
                  Water can travel far from where it enters the roof. We find
                  the true source of the leak to ensure the repair actually
                  works.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Quality Workmanship</h3>
                <p className="text-muted-foreground">
                  We use high-quality sealants and proper techniques. No quick
                  fixes with roofing tar that will just crack again in a few
                  months.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Clear Communication</h3>
                <p className="text-muted-foreground">
                  We explain exactly what went wrong, how we plan to fix it, and
                  provide a fixed-price quote before we start.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Building Permits and Code in Estevan
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Navigating local building codes can be confusing, but we
                  handle it for you. In Estevan, standard residential roof
                  repairs—such as patching leaks, replacing blown-off shingles,
                  or resealing flashing—do not require a building permit.
                  However, if the repair involves fixing structural damage to
                  the roof trusses or replacing large sections of rotted
                  decking, a permit from the City of Estevan may be required. We
                  assess your roof's structural integrity during our free
                  inspection and manage all necessary permitting and compliance,
                  ensuring your repair meets all local and provincial building
                  codes.
                </p>
              </div>
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Insurance and Storm Claims in Estevan
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Estevan is no stranger to severe summer hailstorms and high
                  wind events that can cause localized roof damage. If your roof
                  has been damaged by a storm, dealing with insurance can be
                  stressful. We specialize in storm damage restoration and
                  frequently work with local insurance adjusters. If you suspect
                  wind or hail damage, call us for a free inspection before
                  filing a claim. We will document the damage with detailed
                  photos and provide a comprehensive report for your adjuster,
                  ensuring you get the full coverage you are entitled to for a
                  proper roof repair.
                </p>
              </div>
            </div>

            <div className="mb-20">
              <div className="bg-card p-8 rounded-xl border shadow-sm max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4 text-center">
                  Materials and Warranties for Estevan Roofs
                </h3>
                <p className="text-muted-foreground leading-relaxed text-center mb-6">
                  For Estevan roof repairs, we use premium materials designed to
                  withstand the harsh prairie climate. We source high-grade
                  sealants that remain flexible in sub-zero temperatures and use
                  architectural shingles that closely match your existing roof's
                  color and weathering. We also utilize heavy-gauge metal
                  flashing to permanently seal vulnerable areas around chimneys
                  and vents. All our repair work is backed by a robust
                  workmanship warranty, giving you peace of mind that the leak
                  is fixed for good.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100+
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Repairs in Estevan
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      24h
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Emergency Tarping
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Premium
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Sealants Used
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100%
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Local Crews
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/40828d16-935f-4087-8b3c-2dd81ee3ce2a.png&output=webp&q=60"
                  alt="Estevan roof repair process"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 className="text-3xl font-bold">
                  Our Estevan Roof Repair Process
                </h2>
                <p className="text-lg text-muted-foreground">
                  We aim to make roof repairs as painless as possible for
                  Estevan homeowners.
                </p>
                <ol className="space-y-6 pt-4">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">Free Inspection</h4>
                      <p className="text-muted-foreground mt-1">
                        We safely inspect the damaged area and assess the
                        overall health of your roof.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">Honest Quote</h4>
                      <p className="text-muted-foreground mt-1">
                        You receive a written estimate outlining the repair
                        costs with zero hidden fees.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">Permanent Fix</h4>
                      <p className="text-muted-foreground mt-1">
                        We remove the damaged materials, repair the
                        underlayment, and install new shingles to restore your
                        roof's integrity.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-muted/10 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label="Estevan roofing pages"
              className="bg-background p-6 rounded-xl border shadow-sm"
            >
              <p className="font-bold text-lg mb-4 text-foreground">
                More roofing services in Estevan:
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/areas/estevan"
                    className="text-primary hover:underline font-medium"
                  >
                    ← All Estevan roofing services (overview)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas/estevan/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    Roof Replacement in Estevan
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    All Sask roofing services
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <section className="py-20 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Estevan Roof Repair FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-background rounded-xl border p-2 shadow-sm"
            >
              <AccordionItem value="item-1" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How much does roof repair cost in Estevan?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Roof repairs in Estevan usually cost between $300 and $1,500
                  depending on the severity of the damage. We provide free
                  inspections and detailed, fixed-price quotes.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Can you match my existing shingles?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Yes, we work with all major suppliers to find the closest
                  possible match to your existing shingles, ensuring the repair
                  blends in nicely with the rest of your roof.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Do you repair roof leaks around chimneys?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Yes, chimney flashing failure is a common cause of leaks. We
                  can remove the old, rusted flashing and install new,
                  custom-fit metal flashing to ensure a watertight seal.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Don't Wait for the Next Storm
            </h2>
            <p className="text-xl opacity-90">
              Contact us today for a free inspection and get your Estevan roof
              repaired by professionals.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Get Your Free Repair Quote</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
